import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../../component/Layout";
import api from "../../axios";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

export default function Licenses() {
  const [licenses, setLicenses] = useState([]);
  const [softwares, setSoftwares] = useState([]);
  const [packages, setPackages] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [licenseForm, setLicenseForm] = useState(getEmptyLicense());
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);

  const navigate = useNavigate();

  function getEmptyLicense() {
    return {
      userId: "",
      licenseKey: "",
      softwareId: "",
      packageId: "",
      startDate: "",
      endDate: "",
      maxDeviceCount: 1,
      isActive: true
    };
  }

  useEffect(() => {
    const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      const [licensesRes, softwaresRes, packagesRes, usersRes] = await Promise.all([
        api.get("/api/license"),
        api.get("/api/software"),
        api.get("/api/package"),
        api.get("/api/users")
      ]);
      setLicenses(licensesRes.data);
      setSoftwares(softwaresRes.data);
      setPackages(packagesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const filteredLicenses = licenses.filter((l) => {
    const user = users.find((u) => u.userID === l.userId);
    return user?.fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const openAddModal = () => {
    setLicenseForm(getEmptyLicense());
    setIsAddModalOpen(true);
  };

  const openEditModal = (license) => {
    setSelectedLicenseId(license.id);
    setLicenseForm({ ...license });
    setIsEditModalOpen(true);
  };

  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setLicenseForm(getEmptyLicense());
    setSelectedLicenseId(null);
  };

  const generateLicenseKey = () => {
    const key = uuidv4();
    setLicenseForm((prev) => ({ ...prev, licenseKey: key }));
  };

  const handleSave = async () => {
    try {
      if (isAddModalOpen) {
        await api.post("/api/license", licenseForm);
      } else if (isEditModalOpen) {
        await api.put(`/api/license/${selectedLicenseId}`, licenseForm);
      }
      await fetchAllData();
      closeModals();
    } catch (error) {
      console.error(error);
      alert("İşlem sırasında bir hata oluştu.");
    }
  };

  const handleToggleStatus = async () => {
    try {
      const updatedLicense = { ...licenseForm, isActive: !licenseForm.isActive };
      await api.put(`/api/license/${selectedLicenseId}`, updatedLicense);
      await fetchAllData();
      closeModals();
    } catch (error) {
      console.error(error);
      alert("Durum değiştirilirken bir hata oluştu.");
    }
  };
  const getLicenseStatusText = (license) => {
    const now = new Date();
    const start = license.startDate ? new Date(license.startDate) : null;
    const end = license.endDate ? new Date(license.endDate) : null;
  
    if (start && start > now) return "Henüz Başlamadı";
    if (end && end < now) return "Süresi Doldu";
    if (!license.isActive) return "Pasif";
    return "Aktif";
  };
  
  const getLicenseStatusColor = (license) => {
    const now = new Date();
    const start = license.startDate ? new Date(license.startDate) : null;
    const end = license.endDate ? new Date(license.endDate) : null;
  
    if (start && start > now) return "bg-yellow-100 text-yellow-800";
    if (end && end < now) return "bg-gray-100 text-gray-800";
    if (!license.isActive) return "bg-red-100 text-red-800";
    return "bg-green-100 text-green-800";
  };
  

  return (
    <>
      <Helmet>
        <title>Lisanslar</title>
        <meta name="description" content="Lisans yönetimi sayfası" />
      </Helmet>
      <Layout pageTitle="Lisanslar">
        <div className="flex justify-between items-center mb-6">
          <input
            type="text"
            placeholder="Kullanıcı adına göre ara..."
            className="border p-2 rounded w-1/2"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
            onClick={openAddModal}
          >
            Yeni Lisans Ekle
          </button>
        </div>

        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Lisans Anahtarı</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Kullanıcı</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Yazılım</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Paket</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Başlangıç</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Bitiş</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Max</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-xs text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLicenses.map((license, index) => (
                <tr key={license.id}>
                  <td className="px-6 py-4 text-sm">{index + 1}</td>
                  <td className="px-6 py-4 text-sm break-words">{license.licenseKey}</td>
                  <td className="px-6 py-4 text-sm">
                    {users.find((u) => u.userID === license.userId)?.fullName || "Bilinmiyor"}
                  </td>
                  <td className="px-6 py-4 text-sm">{license.softwareName}</td>
                  <td className="px-6 py-4 text-sm">{license.packageName}</td>
                  <td className="px-6 py-4 text-sm">{license.startDate?.slice(0, 10)}</td>
                  <td className="px-6 py-4 text-sm">{license.endDate?.slice(0, 10)}</td>
                  <td className="px-6 py-4 text-sm">
                    {license.activeDeviceCount} / {license.maxDeviceCount}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getLicenseStatusColor(license)}`}>
                      {getLicenseStatusText(license)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                      onClick={() => openEditModal(license)}
                    >
                      Düzenle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(isAddModalOpen || isEditModalOpen) && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
              <h3 className="text-xl font-bold mb-4">
                {isAddModalOpen ? "Yeni Lisans Ekle" : "Lisans Düzenle"}
              </h3>
              <div className="space-y-4">
                <select
                  className="w-full p-2 border rounded"
                  value={licenseForm.userId}
                  onChange={(e) => setLicenseForm({ ...licenseForm, userId: parseInt(e.target.value) })}
                >
                  <option value="">Kullanıcı Seç</option>
                  {users.map((u) => (
                    <option key={u.userID} value={u.userID}>{u.fullName} ({u.email})</option>
                  ))}
                </select>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    placeholder="Lisans Anahtarı"
                    value={licenseForm.licenseKey}
                    readOnly
                  />
                  <button
                    onClick={generateLicenseKey}
                    className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
                  >
                    Üret
                  </button>
                </div>
                <select
                  className="w-full p-2 border rounded"
                  value={licenseForm.softwareId}
                  onChange={(e) => setLicenseForm({ ...licenseForm, softwareId: parseInt(e.target.value), packageId: "" })}
                >
                  <option value="">Yazılım Seç</option>
                  {softwares.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <select
                  className="w-full p-2 border rounded"
                  value={licenseForm.packageId}
                  onChange={(e) => setLicenseForm({ ...licenseForm, packageId: parseInt(e.target.value) })}
                >
                  <option value="">Paket Seç</option>
                  {packages.filter((p) => p.softwareId === parseInt(licenseForm.softwareId)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    className="w-1/2 p-2 border rounded"
                    value={licenseForm.startDate ? licenseForm.startDate.substring(0,10) : ""}
                    onChange={(e) => setLicenseForm({ ...licenseForm, startDate: e.target.value })}
                  />
                  <input
                    type="date"
                    className="w-1/2 p-2 border rounded"
                    value={licenseForm.endDate ? licenseForm.endDate.substring(0,10) : ""}
                    onChange={(e) => setLicenseForm({ ...licenseForm, endDate: e.target.value })}
                  />
                </div>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  placeholder="Max Cihaz Sayısı"
                  value={licenseForm.maxDeviceCount}
                  onChange={(e) => setLicenseForm({ ...licenseForm, maxDeviceCount: parseInt(e.target.value) })}
                />
                {isEditModalOpen && (
                  <button
                    className={`w-full px-4 py-2 rounded ${licenseForm.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                    onClick={handleToggleStatus}
                  >
                    {licenseForm.isActive ? "Pasife Al" : "Aktife Al"}
                  </button>
                )}
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
                  onClick={closeModals}
                >
                  İptal
                </button>
                <button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
                  onClick={handleSave}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
      </Layout>
    </>
  );
}